import { getChapterByIdOrSlug, getBookById, getNextChapter, getPreviousChapter } from '@/db/queries';
import { parseChapterContent, slugify } from '@/lib/utils';
import type { Book } from '@/db/schema';
import type { ContentBlock } from '@/lib/utils';

<const/id=$global.params.id/>

<try>
  <@placeholder>
    <div class="min-h-screen bg-background">
      <!-- Header Skeleton -->
      <div class="sticky top-0 left-0 right-0 z-50 bg-gray-100/80 backdrop-blur-sm">
        <div class="container mx-auto px-4 h-16 flex items-center justify-between">
          <div class="h-6 w-32 bg-muted rounded animate-pulse"></div>
          <div class="h-6 w-48 bg-muted rounded animate-pulse"></div>
        </div>
      </div>

      <!-- Content Skeleton -->
      <div class="min-h-screen flex items-center justify-center">
        <div class="relative max-w-full py-10 lg:py-20 flex flex-col items-center gap-4">
          <div class="h-96 w-full max-w-2xl bg-muted rounded animate-pulse"></div>
          <div class="h-96 w-full max-w-2xl bg-muted rounded animate-pulse"></div>
        </div>
      </div>
    </div>
  </@placeholder>

  <@catch|error|>
    <div class="min-h-screen bg-background flex items-center justify-center">
      <div class="text-center py-12 px-4">
        <h2 class="text-xl font-semibold mb-2">Failed to load chapter</h2>
        <p class="text-muted-foreground mb-4">
          ${error instanceof Error ? error.message : 'An error occurred while loading the chapter.'}
        </p>
        <a href="/books">
          <button class="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow transition-colors hover:bg-primary/90">
            Browse Books
          </button>
        </a>
      </div>
    </div>
  </@catch>

  <await|chapter|=getChapterByIdOrSlug(id)>
    <if=!chapter>
      <div class="min-h-screen bg-background flex items-center justify-center">
        <div class="text-center py-12 px-4">
          <h2 class="text-xl font-semibold mb-2">Chapter not found</h2>
          <p class="text-muted-foreground mb-4">
            The chapter you're looking for doesn't exist or has been removed.
          </p>
          <a href="/books">
            <button class="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow transition-colors hover:bg-primary/90">
              Browse Books
            </button>
          </a>
        </div>
      </div>
    </if>
    <else>
      <const/bookId=chapter.bookId/>

      <try>
        <await|bookData|=getBookById(bookId)>
          <await|nextChapter|=getNextChapter(bookId, chapter.publishedAt)>
            <await|prevChapter|=getPreviousChapter(bookId, chapter.publishedAt)>
              <const/book=bookData as Book | undefined>
              <const/pages=parseChapterContent(chapter.content) as ContentBlock[]>
              <const/chapterNumber=chapter.number || ''>
              <const/bookSlug=book ? slugify(book.title) : ''/>
              <const/backUrl=book ? `/books/${book.id}/${bookSlug}` : '/books'>
              <const/nextChapterSlug=nextChapter ? slugify(nextChapter.title || '') : ''/>
              <const/nextUrl=nextChapter ? `/chapters/${nextChapter.id}/${nextChapterSlug}` : backUrl>
              <const/prevChapterSlug=prevChapter ? slugify(prevChapter.title || '') : ''/>
              <const/prevUrl=prevChapter ? `/chapters/${prevChapter.id}/${prevChapterSlug}` : ''>

              <div class="min-h-screen bg-background">
                <!-- Header -->
                <div class="sticky top-0 left-0 right-0 z-50 bg-gray-100/80 backdrop-blur-sm border-b">
                  <div class="container mx-auto px-4 h-16 flex items-center justify-between">
                    <div class="flex items-center gap-4">
                      <a href=backUrl class="hover:text-primary flex items-center gap-2">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M18 6 6 18"/>
                          <path d="m6 6 12 12"/>
                        </svg>
                        Back
                      </a>
                      <div>
                        <if=chapterNumber>
                          <h1 class="font-bold">Chapter ${chapterNumber}</h1>
                        </if>
                        <p class="text-sm text-muted-foreground">${chapter.title}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Reader -->
                <div class="min-h-screen flex items-center justify-center">
                  <div class="relative max-w-full py-10 lg:py-20 flex flex-col items-center">
                    <if=pages.length === 0>
                      <div class="text-center py-12">
                        <p class="text-muted-foreground">No content available for this chapter.</p>
                        <p class="text-sm text-muted-foreground mt-2">Content parsing is not yet implemented.</p>
                      </div>
                    </if>
                    <else>
                      <for|page| of=pages>
                        <if=typeof page === 'string'>
                          <p class="font-serif text-lg mb-8 px-4">${page}</p>
                        </if>
                        <else>
                          <const/imageUrl=page.url || '/images/placeholder.svg'>
                          <const/aspectRatio=page.width && page.height ? `${page.width}/${page.height}` : 'auto'>
                          <const/styleAttr=`aspect-ratio: ${aspectRatio};`>
                          <img
                            src=imageUrl
                            style=styleAttr
                            class="pointer-events-none max-w-full"
                            alt="Chapter page"
                          />
                        </else>
                      </for>
                    </else>
                  </div>
                </div>

                <!-- Navigation Controls -->
                <div class="fixed bottom-4 flex flex-col items-center w-full">
                  <div class="flex items-center gap-4 bg-gray-100/80 backdrop-blur-sm rounded-full px-4 mx-4 py-2 shadow-lg border">
                    <if=prevUrl>
                      <a href=prevUrl>
                        <button class="h-10 w-10 inline-flex items-center justify-center rounded-md hover:bg-accent hover:text-accent-foreground cursor-pointer">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="m15 18-6-6 6-6"/>
                          </svg>
                        </button>
                      </a>
                    </if>
                    <else>
                      <button disabled class="h-10 w-10 inline-flex items-center justify-center rounded-md opacity-50 cursor-not-allowed">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="m15 18-6-6 6-6"/>
                        </svg>
                      </button>
                    </else>

                    <span class="min-w-[4rem] text-center text-sm">
                      ${chapter.title}
                    </span>

                    <a href=nextUrl>
                      <button class="h-10 w-10 inline-flex items-center justify-center rounded-md hover:bg-accent hover:text-accent-foreground cursor-pointer">
                        <if=nextChapter>
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="m9 18 6-6-6-6"/>
                          </svg>
                        </if>
                        <else>
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                            <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
                          </svg>
                        </else>
                      </button>
                    </a>
                  </div>
                </div>
              </div>
            </await>
          </await>
        </await>
      </try>
    </else>
  </await>
</try>
